/* Grouping and export for the All sprints page. Kept free of React so the
   rules — which sprint an issue sits in, the order sprints are shown in, what
   a download contains — can be tested on their own. */

export const ALL_SPRINTS = "all";
export const BACKLOG = "backlog";

export const STATUS_META = {
  active: { label: "Active", rank: 0 },
  planned: { label: "Planned", rank: 1 },
  completed: { label: "Completed", rank: 2 },
  backlog: { label: "Backlog", rank: 3 },
};

/* One colour per sprint, used for its left edge, icon and picker dot. Eight
   hues, far enough apart that neighbouring sprints never look alike. */
export const SPRINT_COLORS = [
  { color: "#00ac56", soft: "#e6f7ee" }, // green
  { color: "#1f6fe0", soft: "#eaf2fe" }, // blue
  { color: "#7a5af5", soft: "#f1edfe" }, // violet
  { color: "#e8a200", soft: "#fdf4e0" }, // amber
  { color: "#0fa3a3", soft: "#e2f6f6" }, // teal
  { color: "#e0457b", soft: "#fdeaf1" }, // pink
  { color: "#f07a2c", soft: "#feefe4" }, // orange
  { color: "#4c5fd5", soft: "#eceffd" }, // indigo
];
export const BACKLOG_COLOR = { color: "#8a94a8", soft: "#eef1f6" };

/* The colour follows the sprint, not where it sits in the list, so it stays
   put when the list is filtered or a new sprint is added. Numbered sprints
   ("LMS Sprint 4") take the colour for their number, so consecutive sprints
   always differ; anything else gets one derived from its id. */
export const sprintColorOf = (group) => {
  if (!group || group.id === BACKLOG) return BACKLOG_COLOR;
  const number = String(group.name || "").match(/(\d+)\s*$/);
  if (number) return SPRINT_COLORS[(Number(number[1]) - 1 + SPRINT_COLORS.length) % SPRINT_COLORS.length];
  const id = String(group.id || "");
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return SPRINT_COLORS[hash % SPRINT_COLORS.length];
};

const isDone = (issue) => String(issue?.status || "").toLowerCase() === "done";
const pointsOf = (issue) => Number(issue?.storyPointEstimate) || 0;

/* The sprints endpoint may only send id and name (older API builds), so the
   status and dates fall back to what the issues carry about their sprint. */
const sprintStatusOf = (sprint, details = {}) => {
  const completed = sprint?.completed ?? details?.iscompleted;
  const active = sprint?.active ?? details?.isActive;
  if (completed) return "completed";
  if (active) return "active";
  return "planned";
};

const summarise = (issues) => ({
  total: issues.length,
  done: issues.filter(isDone).length,
  points: issues.reduce((sum, issue) => sum + pointsOf(issue), 0),
  donePoints: issues.filter(isDone).reduce((sum, issue) => sum + pointsOf(issue), 0),
});

/**
 * One group per sprint (including sprints with no issues yet) plus a Backlog
 * group for issues that are not in any sprint. Ordered the way a team reads
 * them: the running sprint, then what is planned next, then completed sprints
 * newest first, and the backlog last.
 */
export const buildSprintGroups = (issues = [], sprints = []) => {
  const groups = new Map();
  const sprintList = Array.isArray(sprints) ? sprints : [];

  sprintList.forEach((sprint, order) => {
    groups.set(String(sprint._id), { id: String(sprint._id), name: sprint.name, sprint, order, issues: [], details: null });
  });

  const backlog = { id: BACKLOG, name: "Backlog", order: Number.MAX_SAFE_INTEGER, issues: [], details: null };

  (Array.isArray(issues) ? issues : []).forEach((issue) => {
    const sprintId = issue?.sprint ? String(issue.sprint) : "";
    const details = issue?.sprintdetails;
    let group = sprintId ? groups.get(sprintId) : null;

    /* an issue pointing at a sprint the list does not have yet (created since
       the list loaded) still belongs under that sprint, not the backlog */
    if (!group && sprintId && details?.name) {
      group = { id: sprintId, name: details.name, sprint: null, order: groups.size, issues: [], details };
      groups.set(sprintId, group);
    }

    if (group) {
      group.details = group.details || details || null;
      group.issues.push(issue);
    } else {
      backlog.issues.push(issue);
    }
  });

  const sprintGroups = [...groups.values()].map((group) => {
    const status = sprintStatusOf(group.sprint, group.details || {});
    return {
      id: group.id,
      name: group.name || "Untitled sprint",
      status,
      startDate: group.sprint?.startDate || group.details?.startDate || null,
      endDate: group.sprint?.endDate || group.details?.endDate || null,
      order: group.order,
      issues: group.issues,
      ...summarise(group.issues),
    };
  });

  sprintGroups.sort((a, b) => {
    const rankGap = STATUS_META[a.status].rank - STATUS_META[b.status].rank;
    if (rankGap) return rankGap;
    // completed: newest first; active and planned: in the order they were made
    return a.status === "completed" ? b.order - a.order : a.order - b.order;
  });

  const result = [...sprintGroups];
  if (backlog.issues.length) {
    result.push({ id: BACKLOG, name: "Backlog", status: "backlog", startDate: null, endDate: null, order: backlog.order, issues: backlog.issues, ...summarise(backlog.issues) });
  }
  return result;
};

export const matchesSearch = (issue, term) => {
  if (!term) return true;
  const text = term.toLowerCase();
  return [issue?.name, issue?.summary, issue?.status, issue?.assignee?.[0]?.employeeName, issue?.label]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(text));
};

const plainText = (html) => {
  if (!html) return "";
  if (typeof document === "undefined") return String(html).replace(/<[^>]*>/g, " ").trim();
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

/* one spreadsheet row per issue; descriptions come from a separate lookup
   because the list endpoint does not carry them */
export const exportRowOf = (issue, sprintName, descriptions = {}) => ({
  Sprint: sprintName,
  "Card ID": issue?.name || "",
  Title: issue?.summary || "",
  Type: issue?.issueType || "",
  Description: plainText(descriptions[issue?._id] ?? issue?.description),
  "Story Points": pointsOf(issue),
  Priority: issue?.priority || "",
  Label: issue?.label || "",
  "Assigned to": issue?.assignee?.[0]?.employeeName || issue?.assignee?.[0]?.employeeEmail || "",
  Status: issue?.status || "",
  Created: formatDate(issue?.createdAt),
  Updated: formatDate(issue?.updatedAt),
});

/* xlsx sheet names: at most 31 characters, none of : \ / ? * [ ], unique */
export const sheetNamesFor = (names) => {
  const used = new Set();
  return names.map((name) => {
    const base = String(name || "Sheet").replace(/[:\\/?*[\]]/g, " ").trim().slice(0, 31) || "Sheet";
    let candidate = base;
    let n = 2;
    while (used.has(candidate.toLowerCase())) {
      const suffix = ` (${n++})`;
      candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    }
    used.add(candidate.toLowerCase());
    return candidate;
  });
};

/* file names are shown to people and saved to disk: keep them readable but safe */
export const fileNameFor = (projectName, scopeName) =>
  `${[projectName, scopeName].filter(Boolean).join(" - ")}`.replace(/[\\/:*?"<>|]+/g, " ").trim() || "Tasks";
